// Dispatches between the mock (offline default) and the real http backend.
//
// Two levels of control:
//
//   1. App-wide — with no `BUN_PUBLIC_API_GATEWAY_URL`, everything is mock.
//   2. Per-domain — with a gateway URL set, `BUN_PUBLIC_MOCK_DOMAINS` keeps
//      named domains on the mock while the rest talk to the real backend, e.g.
//      `BUN_PUBLIC_MOCK_DOMAINS=dashboard,finances`. That is how the founder
//      workspace is built: its aggregates have no endpoints yet, but the pages
//      it drills into (users, orders, shops) must show real data.
//
// Both providers are bundled either way, so this costs nothing at runtime.
// Components import `api` from `@/api` and never know which is active.
import { mock } from "./mock";
import { createHttp } from "./http";
import type { Api } from "./types";

// Bun подставляет `process.env.BUN_PUBLIC_*` литералом при сборке — но только
// само это выражение, дословно. Прежний guard `typeof process !== "undefined"
// ? process.env.X : undefined` в браузере всегда давал undefined: литерал
// подставлялся, а `typeof process` оставался и вычислялся в "undefined".
// Из-за этого консоль молча уходила на моки, даже когда переменная была задана
// в контейнере, и собственник видел выдуманные цифры вместо своих.
//
// try/catch делает то, ради чего guard задумывался: если переменная НЕ задана,
// ссылка `process.env` доживает до браузера и бросает ReferenceError на
// загрузке модуля — здесь она просто означает «адрес не задан, работаем на
// моках». Так же читает адрес client-web, который поэтому и работал.
const readEnv = (read: () => string | undefined): string | undefined => {
	try {
		return read();
	} catch {
		return undefined;
	}
};

const GATEWAY_URL = readEnv(() => process.env.BUN_PUBLIC_API_GATEWAY_URL);
const MOCK_DOMAINS = readEnv(() => process.env.BUN_PUBLIC_MOCK_DOMAINS);

const parseDomains = (raw: string | undefined): Set<keyof Api> => {
	const names = (raw ?? "")
		.split(",")
		.map((name) => name.trim())
		.filter(Boolean);
	const known = new Set(Object.keys(mock) as (keyof Api)[]);
	const selected = new Set<keyof Api>();
	for (const name of names) {
		if (known.has(name as keyof Api)) {
			selected.add(name as keyof Api);
		} else {
			// A typo here silently sends a domain to the real backend, which then
			// 501s or 404s far from the cause. Say so at startup instead.
			console.warn(`[api] BUN_PUBLIC_MOCK_DOMAINS: unknown domain "${name}" — ignored`);
		}
	}
	return selected;
};

/** Take each domain from `mocked` if listed, otherwise from `http`. */
const compose = (http: Api, mocked: Api, domains: Set<keyof Api>): Api => {
	if (domains.size === 0) return http;
	const merged = { ...http };
	for (const domain of domains) {
		// Per-key assignment keeps each domain's methods internally consistent;
		// mixing methods *within* one domain would be a footgun.
		merged[domain] = mocked[domain] as never;
	}
	return merged;
};

export const api: Api = GATEWAY_URL
	? compose(createHttp(GATEWAY_URL), mock, parseDomains(MOCK_DOMAINS))
	: mock;
